import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import TabLayout from "src/components/base/tab-layout";
import SINGLE_GROUP_CONTEXT, { SingleGroupProvider } from "src/context/internal/group";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { ChevronDownIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { GetSingleGroupResponse } from "src/api/generated/frontend_internal_v1";
import { BackButton } from "src/components/base/button";
import UpdateGroupDialog from "src/components/dialogs/update-group";
import DeleteGroupDialog from "src/components/dialogs/delete-group";

/**
 * The properties for {@link SingleGroup}
 */
export type SingleGroupProps = {};

/**
 * The menu for a single group
 */
function SingleGroup(props: SingleGroupProps) {
    const [t] = useTranslation("admin-group-view");
    const [tg] = useTranslation();

    const { groupId } = Route.useParams();
    const navigate = useNavigate();

    const [openUpdateGroup, setOpenUpdateGroup] = React.useState<GetSingleGroupResponse>();
    const [openDeleteGroup, setOpenDeleteGroup] = React.useState<GetSingleGroupResponse>();

    return (
        <SingleGroupProvider uuid={groupId}>
            <SINGLE_GROUP_CONTEXT.Consumer>
                {(ctx) => (
                    <div className={"flex flex-col gap-6"}>
                        <BackButton className={"self-start"} href={"/a/groups/local"}>
                            {t("button.back-to-group-overview")}
                        </BackButton>
                        <TabLayout
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/groups/$groupId/general"}>{t("label.general")}</Tab>
                                    <Tab href={"/a/groups/$groupId/inbound-acls"}>{t("label.inbound-acls")}</Tab>
                                    <Tab href={"/a/groups/$groupId/outbound-acls"}>{t("label.outbound-acls")}</Tab>
                                </TabMenu>
                            }
                            heading={t("heading.group", { group: ctx.group.name })}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton outline={true}>
                                        <span>{tg("button.edit")}</span>
                                        <ChevronDownIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenUpdateGroup(ctx.group)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.update-group")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>

                                        {ctx.group.provider === "Local" && (
                                            <DropdownSection>
                                                <DropdownHeading>{tg("heading.danger-zone")}</DropdownHeading>
                                                <DropdownItem onClick={() => setOpenDeleteGroup(ctx.group)}>
                                                    <TrashIcon />
                                                    <DropdownLabel>{t("button.delete-group")}</DropdownLabel>
                                                </DropdownItem>
                                            </DropdownSection>
                                        )}
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            headingDescription={ctx.group.description}
                        >
                            <Outlet />
                        </TabLayout>

                        {openUpdateGroup && (
                            <Suspense>
                                <UpdateGroupDialog
                                    onClose={() => setOpenUpdateGroup(undefined)}
                                    onUpdate={() => {
                                        setOpenUpdateGroup(undefined);
                                        ctx.reset();
                                    }}
                                    group={ctx.group}
                                />
                            </Suspense>
                        )}

                        {openDeleteGroup && (
                            <Suspense>
                                <DeleteGroupDialog
                                    onClose={() => setOpenDeleteGroup(undefined)}
                                    onDeletion={() =>
                                        navigate({
                                            to: "/a/groups/local",
                                            search: { search: "", page: 1 },
                                        })
                                    }
                                    group={openDeleteGroup}
                                />
                            </Suspense>
                        )}
                    </div>
                )}
            </SINGLE_GROUP_CONTEXT.Consumer>
        </SingleGroupProvider>
    );
}

export const Route = createFileRoute("/_admin/a/groups/$groupId/_group")({
    component: SingleGroup,
});
